import { Divider, useTheme } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useRef } from 'react';
import { FlatList, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import ListItem from '../../Components/ListItem';
import ReadMore from '../../Components/ReadMore';
import { textCategory } from '../../Components/Reservation/constants';
import PageNoResult from '../../Components/Reservation/NoResult';
import {
  ids as reservationStyleIds,
  styles as reservationStyles,
} from '../../Components/Reservation/styles';
import ResponsiveText from '../../Components/ResponsiveText';
import ScreenBanner from '../../Components/ScreenBanner';
import ResponsiveLayout from '../../Components/ScreenWrapper/ResponsiveLayout';
import constants from '../../Config/constants';
import usePageView from '../../Hooks/usePageView';
import { useGetStoreDetailsQuery } from '../../RTK/api/store';
import themeStyle from '../../Theme/styles';

import BookPrivateEvent from './components/BookPrivateEvent';
import FinishMyReservation from './components/FinishMyReservation';
import MapsAndInfo from './components/MapAndInfo';
import Search from './components/Search';
import PageLoader from './PageLoader';
import Reservations from './Reservations';
import {
  ids as reservationStoreStyleIds,
  styles as reservationStoreStyles,
} from './styles';

const ReservationStoreDetails = ({ navigation, route }) => {
  const storeId = route?.params?.storeId ?? route?.params.id;
  // intended not equalto use strict equality (`===`) here because `exclusiveBooking` can be a string '1' from URL params
  const isExclusiveBooking = route?.params?.exclusiveBooking == '1';

  const layoutRef = useRef<ScrollView | FlatList>();
  const stickyHeaderRef = useRef<number>(0);
  const reservationElementYpos = useRef<{ [key: string]: number }>({});
  const theme = useTheme();

  const { data: storeInfo, isLoading } = useGetStoreDetailsQuery(
    { payload: storeId },
    { skip: !storeId },
  );

  // Hooks for going back if url parameter does not include storeId
  useEffect(() => {
    if (!storeId) {
      navigation.goBack();
    } else if (constants.isWeb) {
      // add flag for go back function on checkout page
      // intended not equalto use strict equality (`===`) here because `exclusiveBooking` can be a string '1' from URL params
      if (route.params.exclusiveBooking == '1') {
        sessionStorage.setItem(
          constants.RES_EXCLUSIVE_SESSION_KEY,
          route.params.exclusiveBooking,
        );
      } else {
        sessionStorage.removeItem(constants.RES_EXCLUSIVE_SESSION_KEY);
      }
    }
  }, [storeId, route.params.exclusiveBooking, navigation]);

  usePageView({
    enabled: !isLoading,
    page_store_id: storeInfo?.id,
    screen_class: 'ReservationStoreDetails',
    screenName: `Reservation | Store | ${storeInfo?.name}`,
  });

  const _onSearch = useCallback(() => {
    const marginAndSpacing = 30; // 10 - top spacing of the container of details, 20 divider top spacing
    const scrollTo = Object.values(reservationElementYpos.current).reduce(
      (acc, height) => acc + height,
      marginAndSpacing - stickyHeaderRef.current,
    );
    if ('scrollTo' in layoutRef.current) {
      // scrollview (mobile)
      layoutRef.current.scrollTo({
        y: scrollTo,
        animated: true,
      });
    } else if ('scrollToOffset' in layoutRef.current) {
      // flatlist (web)
      layoutRef.current.scrollToOffset({
        offset: scrollTo,
        animated: true,
      });
    }
  }, []);

  if (isLoading) {
    return <PageLoader />;
  } else if (lodashIsEmpty(storeInfo)) {
    return <PageNoResult />;
  }

  return (
    <ResponsiveLayout
      onRef={(r) => (layoutRef.current = r)}
      title={constants.isWeb ? '' : storeInfo.name}
      titleProps={{
        onLayout: (event) => {
          const layout = event.nativeEvent.layout;
          stickyHeaderRef.current = layout.height;
        },
      }}
      LeftComponent={
        <View
          style={[
            themeStyle.flex1,
            isExclusiveBooking && themeStyle.spacingTop,
          ]}
        >
          <View
            style={reservationStoreStyles.bannerContainer}
            onLayout={(event) => {
              const layout = event.nativeEvent.layout;
              reservationElementYpos.current.banner = layout.height;
            }}
          >
            {/* @ts-ignore: store_logo is not required, the file is just a js type and cannot add type checking on the props */}
            <ScreenBanner banner={storeInfo.banner} webHeight={310} />
          </View>
          <View style={reservationStyles.contentContainer}>
            <View
              dataSet={{ media: reservationStoreStyleIds.detailsContainer }}
              style={reservationStoreStyles.detailsContainer}
              onLayout={(event) => {
                const layout = event.nativeEvent.layout;
                reservationElementYpos.current.details = layout.height;
              }}
            >
              {/* Store Name */}
              <ResponsiveText
                category={textCategory.LARGE_HEADING}
                numberOfLines={3}
                style={[themeStyle.bold]}
                adjustsFontSizeToFit
              >
                {storeInfo.name}
              </ResponsiveText>
              {!lodashIsEmpty(storeInfo.store_tags) && (
                <ResponsiveText
                  category={textCategory.TITLE}
                  status="primary"
                  style={[themeStyle.spacingBottomMedium, themeStyle.bold]}
                >
                  {storeInfo.location} ·
                  <ResponsiveText
                    category={textCategory.TITLE}
                    status="primary"
                    style={[themeStyle.capitalize, themeStyle.bold]}
                  >
                    {' '}
                    {storeInfo.store_tags.join(' · ')}
                  </ResponsiveText>
                </ResponsiveText>
              )}

              <ReadMore
                category={textCategory.BODY}
                contentContainerStyle={themeStyle.spacingBottom}
                maxChar={300}
                status="info"
                value={storeInfo?.store_description}
              />
              <ResponsiveText
                category={textCategory.HEADING}
                style={[themeStyle.bold, themeStyle.spacingBottomMedium]}
              >
                Dress Code
              </ResponsiveText>
              <ListItem
                leftIcon={{ name: 'checkmark' }}
                title={
                  <ResponsiveText category={textCategory.BODY}>
                    Casual
                  </ResponsiveText>
                }
              />
              {Boolean(storeInfo?.reservation_info?.value) && (
                <View
                  style={[
                    themeStyle.pageVerticalSpacingMedium,
                    themeStyle.pageHorizontalSpacingSmall,
                    themeStyle.spacingTopMedium,
                    reservationStoreStyles.borderRadius,
                    { backgroundColor: theme['color-control-active'] },
                  ]}
                >
                  <ListItem
                    leftIcon={{ name: 'alert-circle-outline' }}
                    contentContainerStyle={themeStyle.alignItemsStart}
                    title={
                      <ResponsiveText category={textCategory.BODY}>
                        {storeInfo.reservation_info.value}
                      </ResponsiveText>
                    }
                  />
                </View>
              )}
            </View>
            <Divider
              dataSet={{ media: reservationStyleIds.sectionDivider }}
              style={[
                themeStyle.spacingTop,
                themeStyle.spacingBottomMedium,
                themeStyle.sectionSeparator,
                reservationStyles.sectionDivider,
              ]}
            />
            <Reservations storeId={storeId} />
            <BookPrivateEvent
              socialLink={storeInfo?.reservation_contact?.value}
            />
            <Divider
              dataSet={{ media: reservationStyleIds.sectionDivider }}
              style={[
                themeStyle.sectionSeparator,
                reservationStyles.sectionDivider,
              ]}
            />
            <MapsAndInfo
              latitude={storeInfo.geocode_location?.lat}
              location_address={storeInfo.location}
              longitude={storeInfo.geocode_location?.lng}
              name={storeInfo.name}
              storeId={storeId}
            />
          </View>
        </View>
      }
      RightComponent={
        <View
          dataSet={{ media: reservationStyleIds.boxContainer }}
          style={reservationStyles.boxContainer}
        >
          <Search storeId={storeId} onSearch={_onSearch} />
          <FinishMyReservation storeId={storeId} />
        </View>
      }
      MobileBottomComponent={
        <View
          dataSet={{ media: reservationStyleIds.boxContainer }}
          style={reservationStyles.boxContainer}
        >
          <FinishMyReservation storeId={storeId} />
          <Search storeId={storeId} onSearch={_onSearch} />
        </View>
      }
      backgroundColor={null} // to remove safearea on the top
      rightComponentWidth={285}
      stickyMobile
    />
  );
};

export default ReservationStoreDetails;
